import React, { useState, useEffect } from 'react';
import {
  BadgeCheck,
  Layers,
  Thermometer,
  Plus,
  Search,
  Edit2,
  Trash2,
  AlertCircle,
  Loader2,
  X,
  Paperclip,
  Download,
  FileText,
  CheckCircle,
} from 'lucide-react';
import { qualificationsApi, usersApi } from '../api';
import { toast } from 'sonner';
import { extractApiError, validateDateFields, formatDateBR } from '../utils/dateValidation';

// Página "Qualificações" — módulo Regulatório.
// Duas abas independentes (Qualificação / Qualificação Térmica) com a mesma
// estrutura: cada registro pode receber vários certificados anexados ao
// longo do tempo (histórico de renovações); o vencimento acompanhado é o do
// certificado mais recente.
const TABS = [
  { id: 'qualificacao', label: 'Qualificação', icon: Layers },
  { id: 'termica', label: 'Qualificação Térmica', icon: Thermometer },
];

const STATUS_LABELS = {
  regular: { label: 'Regular', color: 'bg-green-500/20 text-green-400' },
  a_vencer: { label: 'A Vencer', color: 'bg-yellow-500/20 text-yellow-400' },
  vencido: { label: 'Vencido', color: 'bg-red-500/20 text-red-400' },
};

const Qualificacoes = () => {
  const [activeTab, setActiveTab] = useState(TABS[0].id);

  return (
    <div className="space-y-6" data-testid="qualificacoes-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
          <BadgeCheck className="text-green-500" />
          Qualificações
        </h1>
        <p className="text-gray-400 mt-1">Certificados de qualificação e acompanhamento de vencimento</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? 'border-green-400 text-green-400'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {TABS.map((tab) => (
        activeTab === tab.id && <QualificationTypeTab key={tab.id} type={tab.id} label={tab.label} />
      ))}
    </div>
  );
};

const QualificationTypeTab = ({ type, label }) => {
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState({ total: 0, overdue: 0, due_soon: 0, regular: 0, no_certificate: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [certificatesRecord, setCertificatesRecord] = useState(null);

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, searchTerm]);

  const loadUsers = async () => {
    try {
      const res = await usersApi.list({ limit: 100 });
      setUsers(res.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadRecords = async () => {
    try {
      setLoading(true);
      const [recordsRes, statsRes] = await Promise.all([
        qualificationsApi.list({ type, search: searchTerm || undefined }),
        qualificationsApi.stats({ type }),
      ]);
      setRecords(recordsRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar registros');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRecord = async (data) => {
    try {
      await qualificationsApi.create({ ...data, type });
      toast.success('Registro criado com sucesso');
      setShowRecordModal(false);
      loadRecords();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao criar registro'));
    }
  };

  const handleUpdateRecord = async (data) => {
    try {
      await qualificationsApi.update(editingRecord.id, data);
      toast.success('Registro atualizado com sucesso');
      setShowRecordModal(false);
      setEditingRecord(null);
      loadRecords();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao atualizar'));
    }
  };

  const handleDeleteRecord = async (record) => {
    if (!window.confirm(`Excluir o registro "${record.name}"?`)) return;
    try {
      await qualificationsApi.delete(record.id);
      toast.success('Registro excluído');
      loadRecords();
    } catch (error) {
      toast.error('Erro ao excluir registro');
    }
  };

  // Keeps the certificates panel in sync with the latest data after upload/delete
  const refreshCertificatesRecord = async () => {
    const res = await qualificationsApi.list({ type });
    setRecords(res.data || []);
    const updated = (res.data || []).find((r) => r.id === certificatesRecord?.id);
    setCertificatesRecord(updated || null);
    const statsRes = await qualificationsApi.stats({ type });
    setStats(statsRes.data);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-sm text-gray-400">Registros de {label.toLowerCase()} e seus certificados</p>
        <button
          onClick={() => { setEditingRecord(null); setShowRecordModal(true); }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={20} />
          Novo Registro
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Total de Registros</p>
          <p className="text-2xl font-bold text-white">{stats.total}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Vencidos</p>
          <p className="text-2xl font-bold text-red-400">{stats.overdue}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">A Vencer (30 dias)</p>
          <p className="text-2xl font-bold text-yellow-400">{stats.due_soon}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Sem Certificado</p>
          <p className="text-2xl font-bold text-white">{stats.no_certificate}</p>
        </div>
      </div>

      {/* Records table */}
      <div className="card overflow-hidden">
        <div className="p-3 border-b border-gray-700">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Buscar por nome..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Responsável</th>
                <th>Situação</th>
                <th>Vencimento</th>
                <th>Certificados</th>
                <th className="text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="py-12 text-center">
                    <Loader2 className="animate-spin text-green-500 inline" size={28} />
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-12 text-center text-gray-500">Nenhum registro encontrado</td>
                </tr>
              ) : (
                records.map((record) => (
                  <tr key={record.id}>
                    <td className="text-white font-medium">{record.name}</td>
                    <td className="text-gray-400">{record.responsible_name || '-'}</td>
                    <td>
                      {record.status ? (
                        <span className={`px-2 py-1 rounded-full text-xs ${STATUS_LABELS[record.status]?.color || 'bg-gray-500/20 text-gray-400'}`}>
                          {STATUS_LABELS[record.status]?.label || record.status}
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs bg-gray-500/20 text-gray-400">Sem certificado</span>
                      )}
                    </td>
                    <td>
                      <span className={record.status === 'vencido' ? 'text-red-400 font-semibold' : 'text-gray-400'}>
                        {formatDateBR(record.expiry_date)}
                        {record.status === 'vencido' && <AlertCircle size={14} className="inline ml-1" />}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => setCertificatesRecord(record)}
                        className="flex items-center gap-1 text-blue-400 hover:text-blue-300"
                        title="Ver / anexar certificados"
                      >
                        <Paperclip size={14} />
                        {record.certificates_count} {record.certificates_count === 1 ? 'certificado' : 'certificados'}
                      </button>
                    </td>
                    <td>
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => { setEditingRecord(record); setShowRecordModal(true); }} className="p-1 text-gray-400 hover:text-blue-400" title="Editar">
                          <Edit2 size={18} />
                        </button>
                        <button onClick={() => handleDeleteRecord(record)} className="p-1 text-gray-400 hover:text-red-400" title="Excluir">
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Record Modal */}
      {showRecordModal && (
        <RecordModal
          record={editingRecord}
          users={users}
          onClose={() => { setShowRecordModal(false); setEditingRecord(null); }}
          onSave={editingRecord ? handleUpdateRecord : handleCreateRecord}
        />
      )}

      {/* Certificates Modal */}
      {certificatesRecord && (
        <CertificatesModal
          record={certificatesRecord}
          onClose={() => setCertificatesRecord(null)}
          onChanged={refreshCertificatesRecord}
        />
      )}
    </div>
  );
};

const RecordModal = ({ record, users, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: record?.name || '',
    responsible_id: record?.responsible_id || '',
    observations: record?.observations || '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = { ...formData };
      if (!data.responsible_id) delete data.responsible_id;
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">{record ? 'Editar Registro' : 'Novo Registro'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Nome *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input w-full"
              placeholder="Ex: Empresa/Equipamento/Processo qualificado"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Responsável Interno</label>
            <select
              value={formData.responsible_id}
              onChange={(e) => setFormData({ ...formData, responsible_id: e.target.value })}
              className="input w-full"
            >
              <option value="">Selecione...</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>{user.full_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Observações</label>
            <textarea
              value={formData.observations}
              onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
              className="input w-full"
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
              {saving ? <Loader2 className="animate-spin" size={20} /> : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const CertificatesModal = ({ record, onClose, onChanged }) => {
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Selecione o arquivo do certificado'); return; }
    const dateError = validateDateFields([
      { value: issueDate, label: 'Data de Emissão' },
      { value: expiryDate, label: 'Data de Vencimento' },
    ]);
    if (dateError) { toast.error(dateError); return; }
    setUploading(true);
    try {
      await qualificationsApi.uploadCertificate(record.id, file, { issue_date: issueDate, expiry_date: expiryDate });
      toast.success('Certificado anexado com sucesso');
      setIssueDate('');
      setExpiryDate('');
      setFile(null);
      await onChanged();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao anexar certificado'));
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (certificate) => {
    setBusyId(`download-${certificate.id}`);
    try {
      const res = await qualificationsApi.downloadCertificate(certificate.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', certificate.file_name || `certificado_${certificate.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar certificado');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (certificate) => {
    if (!window.confirm('Remover este certificado do histórico?')) return;
    setBusyId(`delete-${certificate.id}`);
    try {
      await qualificationsApi.deleteCertificate(certificate.id);
      toast.success('Certificado removido');
      await onChanged();
    } catch (error) {
      toast.error('Erro ao remover certificado');
    } finally {
      setBusyId(null);
    }
  };

  const certificates = record.certificates || [];

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-white">Certificados</h2>
            <p className="text-sm text-gray-400">{record.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        {/* Upload new certificate */}
        <form onSubmit={handleUpload} className="space-y-4 p-4 rounded-lg border border-gray-700 bg-gray-800/40 mb-6">
          <p className="text-sm font-medium text-gray-300 flex items-center gap-2">
            <Paperclip size={16} className="text-green-500" />
            Anexar novo certificado
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Data de Emissão</label>
              <input
                type="date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                className="input w-full"
                min="1900-01-01"
                max="2099-12-31"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Data de Vencimento</label>
              <input
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                className="input w-full"
                min="1900-01-01"
                max="2099-12-31"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Arquivo *</label>
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="input w-full"
            />
          </div>
          <div className="flex justify-end">
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={uploading}>
              {uploading ? <Loader2 className="animate-spin" size={18} /> : <Paperclip size={18} />}
              Anexar Certificado
            </button>
          </div>
        </form>

        {/* History */}
        <div>
          <p className="text-sm font-medium text-gray-300 mb-2">Histórico de certificados</p>
          {certificates.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">Nenhum certificado anexado ainda</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {certificates.map((cert) => (
                <div key={cert.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-700">
                  <div className="min-w-0 flex items-center gap-2">
                    <FileText size={18} className="text-green-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate" title={cert.file_name}>{cert.file_name}</p>
                      <p className="text-xs text-gray-500">
                        Emissão: {formatDateBR(cert.issue_date)} · Vencimento: {formatDateBR(cert.expiry_date)}
                        {cert.uploaded_by_name && <> · Anexado por {cert.uploaded_by_name}</>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleDownload(cert)}
                      disabled={busyId === `download-${cert.id}`}
                      className="p-2 text-gray-400 hover:text-blue-400"
                      title="Baixar"
                    >
                      {busyId === `download-${cert.id}` ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                    </button>
                    <button
                      onClick={() => handleDelete(cert)}
                      disabled={busyId === `delete-${cert.id}`}
                      className="p-2 text-gray-400 hover:text-red-400"
                      title="Remover"
                    >
                      {busyId === `delete-${cert.id}` ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 mt-4 border-t border-gray-700">
          <button onClick={onClose} className="btn-secondary flex items-center gap-2">
            <CheckCircle size={18} />
            Concluído
          </button>
        </div>
      </div>
    </>
  );
};

export default Qualificacoes;
